import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDocument, updateDocument, getCollection, addDocument } from '../services/firestoreService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { trackMetaEventServer } from '../services/metaPixel';
import { Box, Typography, CircularProgress, Alert, Grid, TextField, Button, Card, CardContent, CardHeader, MenuItem, Snackbar, Divider, Chip, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, IconButton, ListItemSecondaryAction } from '@mui/material';
import { validateRut } from '../utils/validators';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { generateICSFile, downloadICSFile, generateGoogleCalendarLink, generateEmailTemplate, copyToClipboard } from '../utils/calendarUtils';

// Datos de regiones y comunas de Chile
import { REGIONES_COMUNAS } from '../utils/regionData';
import { todayLocalStr } from '../utils/dateUtils';
export { REGIONES_COMUNAS };

const LEAD_COLORS = {
  Externo: '#2196f3',
  Manual: '#2196f3',
  Web: '#2196f3',
  Presupuesto: '#ff9800',
  'Agendamiento Online': '#4caf50',
  Diagnóstico: '#f44336',
};
const getLeadColor = (source) => LEAD_COLORS[source] || '#9e9e9e';

const ContactRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { branchName } = useBranch();

  const [request, setRequest] = useState(null);
  const [patient, setPatient] = useState(null);
  const [owner, setOwner] = useState(null);
  const [clinicalRecord, setClinicalRecord] = useState(null);

  const [formData, setFormData] = useState({});
  const [breeds, setBreeds] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [users, setUsers] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [newLogText, setNewLogText] = useState('');
  const [rutError, setRutError] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeDialogReason, setCloseDialogReason] = useState('');
  
  // Estados para el modal de presupuesto
  const [openBudgetDialog, setOpenBudgetDialog] = useState(false);
  const [budgetNumber, setBudgetNumber] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetObservation, setBudgetObservation] = useState('');
  const [budgetProcedure, setBudgetProcedure] = useState('');
  const [budgetPaid, setBudgetPaid] = useState(false);
  const [budgetReceiptNumber, setBudgetReceiptNumber] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [sales, setSales] = useState([]);
  const [editingBudget, setEditingBudget] = useState(null); // Para editar presupuestos existentes

  // Estados para el modal de teleconsulta
  const [openTeleconsultDialog, setOpenTeleconsultDialog] = useState(false);
  const [teleconsultDate, setTeleconsultDate] = useState('');
  const [teleconsultTime, setTeleconsultTime] = useState('');
  const [teleconsultDuration, setTeleconsultDuration] = useState(30);
  const [teleconsultReason, setTeleconsultReason] = useState('');
  const [teleconsultNotes, setTeleconsultNotes] = useState('');
  const [teleconsults, setTeleconsults] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [currentTeleconsult, setCurrentTeleconsult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requestData = await getDocument('contact-requests', id);
      if (!requestData) {
        setError('No se encontró la solicitud.');
        setLoading(false);
        return;
      }
      setRequest(requestData);

      const patientData = requestData.patientId ? await getDocument('patients', requestData.patientId) : null;
      setPatient(patientData);

      const ownerId = requestData.ownerId || patientData?.ownerId;
      const ownerData = ownerId ? await getDocument('owners', ownerId) : null;
      setOwner(ownerData);

      if (requestData.originType === 'clinicalRecord' && requestData.originId) {
        try {
          const crData = await getDocument('clinicalRecords', requestData.originId);
          setClinicalRecord(crData);
        } catch (crErr) {
          console.error('Error cargando ficha SOAP:', crErr);
          setClinicalRecord(null);
        }
      } else {
        setClinicalRecord(null);
      }

      // Initialize form data
      setFormData({
        patientName: patientData?.name || '',
        birthDate: patientData?.birthDate || '',
        edad: patientData?.edad || '',
        species: patientData?.species || '',
        breed: patientData?.breed || '',
        genero: patientData?.genero || '',
        ownerName: ownerData?.name || '',
        rut: ownerData?.rut || '',
        celular: ownerData?.celular || '',
        mail: ownerData?.mail || '',
        region: ownerData?.region || '',
        comuna: ownerData?.comuna || '',
        convenio: ownerData?.convenio || '',
        assignedTo: requestData.assignedTo || '',
        attentionNeed: requestData.attentionNeed || '',
        status: requestData.status || 'Nuevo',
        priority: requestData.priority || 'Normal',
        closeReason: requestData.closeReason || '',
        nextAction: requestData.nextAction || '',
        nextActionDate: requestData.nextActionDate || '',
      });

      // Cargar bitácora
      setLogs(requestData.logs || []);
      
      // Cargar teleconsultas
      setTeleconsults(requestData.teleconsults || []);

      // Cargar presupuestos y ventas reales vinculados
      const presupuestoQueries = [];
      if (id) presupuestoQueries.push(getDocs(query(collection(db, 'presupuestos'), where('contactRequestId', '==', id))));
      if (requestData.patientId) presupuestoQueries.push(getDocs(query(collection(db, 'presupuestos'), where('patientId', '==', requestData.patientId))));
      const mergedPre = new Map();
      for (const q of presupuestoQueries) {
        const snap = await q;
        snap.docs.forEach((d) => { if (!mergedPre.has(d.id)) mergedPre.set(d.id, { ...d.data(), id: d.id }); });
      }
      setPresupuestos([...mergedPre.values()].sort((a, b) => {
        const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return db - da;
      }));

      if (requestData.patientId) {
        const salesSnap = await getDocs(query(collection(db, 'Sales'), where('patientId', '==', requestData.patientId)));
        setSales(salesSnap.docs.map((d) => ({ ...d.data(), id: d.id })).sort((a, b) => {
          const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return db - da;
        }));
      } else {
        setSales([]);
      }

    } catch (err) {
      setError('Error al cargar los detalles de la solicitud.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cargar razas, convenios, usuarios y necesidades al montar el componente
  useEffect(() => {
    const fetchBreeds = async () => {
      try {
        const breedsData = await getCollection('breeds');
        console.log('Razas cargadas:', breedsData.length, breedsData);
        setBreeds(breedsData);
      } catch (error) {
        console.error('Error loading breeds:', error);
      }
    };
    
    const fetchConvenios = async () => {
      try {
        const conveniosData = await getCollection('convenios');
        console.log('Convenios cargados:', conveniosData.length, conveniosData);
        setConvenios(conveniosData.filter(c => c.isActive)); // Solo activos
      } catch (error) {
        console.error('Error loading convenios:', error);
      }
    };
    
    const fetchUsers = async () => {
      try {
        const usersData = await getCollection('users');
        console.log('Usuarios cargados:', usersData.length, usersData);
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    
    const fetchNeeds = async () => {
      try {
        const needsData = await getCollection('needs');
        console.log('Necesidades cargadas:', needsData.length, needsData);
        setNeeds(needsData.filter(n => n.isActive)); // Solo activas
      } catch (error) {
        console.error('Error loading needs:', error);
      }
    };
    
    const fetchProcedures = async () => {
      try {
        const proceduresData = await getCollection('surgicalProcedures');
        console.log('Procedimientos cargados:', proceduresData.length, proceduresData);
        setProcedures(proceduresData.filter(p => p.active !== false)); // Solo activos
      } catch (error) {
        console.error('Error loading procedures:', error);
      }
    };
    
    fetchBreeds();
    fetchConvenios();
    fetchUsers();
    fetchNeeds();
    fetchProcedures();
  }, []);

  // Función para calcular edad desde fecha de nacimiento
  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Si cambia la fecha de nacimiento, calcular edad automáticamente
    if (name === 'birthDate') {
      const calculatedAge = calculateAge(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        edad: calculatedAge
      }));
    } else if (name === 'species') {
      // Si cambia la especie, limpiar la raza
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        breed: ''
      }));
    } else if (name === 'region') {
      // Si cambia la región, limpiar la comuna
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        comuna: ''
      }));
    } else if (name === 'rut') {
      // Validar RUT chileno
      setFormData(prev => ({ ...prev, [name]: value }));
      if (value && value.length > 0) {
        if (validateRut(value)) {
          setRutError('');
        } else {
          setRutError('RUT inválido. Formato: 12345678-9');
        }
      } else {
        setRutError('');
      }
    } else if (name === 'status') {
      setFormData(prev => ({
        ...prev,
        status: value,
        closeReason: value === 'Cerrado' ? prev.closeReason || '' : '',
      }));
    } else if (name === 'assignedTo') {
      // Si se asigna a alguien, cambiar estado a "Asignado" automáticamente
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        status: value ? 'Asignado' : prev.status // Solo cambiar si hay un valor
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCloseCase = () => {
    setCloseDialogOpen(true);
    setCloseDialogReason(formData.closeReason || '');
  };

  const confirmCloseCase = async () => {
    if (!closeDialogReason) {
      setSnackbar({ open: true, message: 'Por favor selecciona un motivo de cierre antes de cerrar el caso', severity: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const newLog = {
        userName: userData?.name || userData?.email || 'Usuario',
        userEmail: currentUser?.email || '',
        timestamp: new Date(),
        text: `Caso cerrado. Motivo: ${closeDialogReason}`,
      };
      const updatedLogs = [...logs, newLog];
      await updateDocument('contact-requests', id, {
        status: 'Cerrado',
        closeReason: closeDialogReason,
        logs: updatedLogs,
        updatedAt: new Date(),
      });
      setLogs(updatedLogs);
      setFormData((prev) => ({ ...prev, status: 'Cerrado', closeReason: closeDialogReason }));
      setCloseDialogOpen(false);
      setSnackbar({ open: true, message: 'Caso cerrado correctamente', severity: 'success' });
    } catch (err) {
      console.error('Error cerrando caso:', err);
      setSnackbar({ open: true, message: 'Error al cerrar el caso', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (formData.status === 'Cerrado' && !formData.closeReason) {
        setSnackbar({ open: true, message: 'Por favor selecciona un motivo de cierre para cerrar la solicitud', severity: 'warning' });
        setLoading(false);
        return;
      }

      // Update patient data
      if (patient) {
        await updateDocument('patients', patient.id, {
          name: formData.patientName,
          breed: formData.breed,
          genero: formData.genero,
          birthDate: formData.birthDate,
          edad: formData.edad,
          species: formData.species,
        });
      }

      // Update owner data
      if (owner) {
        await updateDocument('owners', owner.id, {
          name: formData.ownerName,
          rut: formData.rut,
          celular: formData.celular,
          mail: formData.mail,
          region: formData.region,
          comuna: formData.comuna,
          convenio: formData.convenio,
        });
      }

      // Bitácora automática de asignación
      const newLogs = [...logs];
      if (formData.assignedTo !== (request?.assignedTo || '')) {
        const prevName = users.find(u => u.email === (request?.assignedTo || ''))?.name || request?.assignedTo || 'Sin asignar';
        const nextName = users.find(u => u.email === formData.assignedTo)?.name || formData.assignedTo || 'Sin asignar';
        newLogs.push({
          userName: userData?.name || userData?.email || 'Usuario',
          userEmail: currentUser?.email || '',
          timestamp: new Date(),
          text: `Cambio de ejecutivo: ${prevName} → ${nextName}`,
        });
      }

      // Update request data
      await updateDocument('contact-requests', id, {
        assignedTo: formData.assignedTo,
        attentionNeed: formData.attentionNeed,
        status: formData.status,
        priority: formData.priority,
        closeReason: formData.closeReason || '',
        nextAction: formData.nextAction || '',
        nextActionDate: formData.nextActionDate || '',
        ownerName: formData.ownerName,
        logs: newLogs,
      });

      setLogs(newLogs);
      setSnackbar({ open: true, message: 'Datos guardados con éxito', severity: 'success' });
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Error saving data:', err);
      setSnackbar({ open: true, message: 'Error al guardar los datos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLog = async () => {
    if (!newLogText.trim()) {
      setSnackbar({ open: true, message: 'Por favor ingrese un texto para la bitácora', severity: 'warning' });
      return;
    }

    try {
      const newLog = {
        userName: userData?.name || userData?.email || 'Usuario',
        userEmail: currentUser?.email || '',
        timestamp: new Date(),
        text: newLogText.trim()
      };

      const updatedLogs = [...logs, newLog];

      // Actualizar en Firestore
      await updateDocument('contact-requests', id, {
        logs: updatedLogs
      });

      // Actualizar estado local
      setLogs(updatedLogs);
      setNewLogText('');
      setSnackbar({ open: true, message: 'Entrada agregada a la bitácora', severity: 'success' });
    } catch (error) {
      console.error('Error adding log:', error);
      setSnackbar({ open: true, message: 'Error al agregar entrada a la bitácora', severity: 'error' });
    }
  };

  const handleOpenBudgetDialog = () => {
    setOpenBudgetDialog(true);
  };

  const handleCloseBudgetDialog = () => {
    setOpenBudgetDialog(false);
    // Limpiar campos
    setBudgetNumber('');
    setBudgetAmount('');
    setBudgetObservation('');
    setBudgetProcedure('');
    setBudgetPaid(false);
    setBudgetReceiptNumber('');
    setEditingBudget(null);
  };

  const handleEditBudget = (budget) => {
    // Cargar datos del presupuesto a editar
    setBudgetNumber(budget.number);
    setBudgetAmount(formatCurrency(budget.amount));
    setBudgetObservation(budget.observation || '');
    setBudgetProcedure(budget.procedureId || '');
    setBudgetPaid(budget.paid || false);
    setBudgetReceiptNumber(budget.receiptNumber || '');
    setEditingBudget(budget);
    setOpenBudgetDialog(true);
  };

  const formatCurrency = (value) => {
    // Eliminar todo excepto números
    const numbers = value.replace(/\D/g, '');
    // Formatear con separadores de miles
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleBudgetAmountChange = (e) => {
    const formatted = formatCurrency(e.target.value);
    setBudgetAmount(formatted);
  };

  const handleAddBudget = async () => {
    if (!budgetNumber.trim() || !budgetAmount.trim()) {
      setSnackbar({ open: true, message: 'Por favor complete N° y Valor del presupuesto', severity: 'warning' });
      return;
    }

    if (budgetPaid && !budgetReceiptNumber.trim()) {
      setSnackbar({ open: true, message: 'Si el presupuesto está pagado, debe ingresar el número de boleta', severity: 'warning' });
      return;
    }

    if (!request?.patientId && !editingBudget) {
      setSnackbar({ open: true, message: 'No hay paciente asociado a esta solicitud', severity: 'error' });
      return;
    }

    try {
      // Obtener información del procedimiento si está seleccionado
      const selectedProcedure = budgetProcedure ? procedures.find(p => p.id === budgetProcedure) : null;
      
      const budgetData = {
        number: budgetNumber.trim(),
        amount: budgetAmount.replace(/\./g, ''), // Guardar sin puntos
        observation: budgetObservation.trim(),
        procedureId: budgetProcedure || '',
        procedureName: selectedProcedure?.name || '',
        procedureCategory: selectedProcedure?.category || '',
        paid: budgetPaid,
        receiptNumber: budgetPaid ? budgetReceiptNumber.trim() : '',
        paidAt: budgetPaid ? new Date() : null
      };

      let budgetLogText = '';
      let isNewPayment = false;

      if (editingBudget) {
        // ACTUALIZAR presupuesto existente
        isNewPayment = !editingBudget.paid && budgetPaid; // Si antes no estaba pagado y ahora sí
        
        await updateDocument('budgets', editingBudget.id, budgetData);

        budgetLogText = `Presupuesto actualizado:\nN° Presupuesto: ${budgetNumber.trim()}\nValor: $${budgetAmount}${selectedProcedure ? `\nProcedimiento: ${selectedProcedure.name}` : ''}${budgetObservation.trim() ? `\nObservación: ${budgetObservation.trim()}` : ''}`;
        
        if (isNewPayment) {
          budgetLogText += `\n\n✅ PRESUPUESTO MARCADO COMO PAGADO\nN° Boleta: ${budgetReceiptNumber.trim()}`;
        }
      } else {
        // CREAR nuevo presupuesto
        const newBudget = {
          ...budgetData,
          patientId: request.patientId,
          requestId: id,
          patientName: patient?.name || '',
          ownerName: owner?.name || owner?.fullName || '',
          attentionNeed: formData.attentionNeed || '',
          userName: userData?.name || userData?.email || 'Usuario',
          userEmail: currentUser?.email || '',
          createdAt: new Date(),
          status: 'Enviado'
        };

        await addDocument('budgets', newBudget);

        // Meta Pixel: AddToCart al crear un nuevo presupuesto (Conversions API)
        const amountNumeric = parseFloat(budgetAmount.replace(/\./g, '')) || 0;
        trackMetaEventServer({
          eventName: 'AddToCart',
          value: amountNumeric,
          currency: 'CLP',
          owner: { name: owner?.name, rut: owner?.rut, celular: owner?.celular, mail: owner?.mail || owner?.contact, ownerId: owner?.id },
          itemName: selectedProcedure?.name || formData.attentionNeed || 'Presupuesto Veterinario',
          contentIds: [budgetNumber.trim()],
          eventId: `addtocart_${budgetNumber.trim()}`,
        }).catch(() => {});

        budgetLogText = `Presupuesto enviado:\nN° Presupuesto: ${budgetNumber.trim()}\nValor: $${budgetAmount}${selectedProcedure ? `\nProcedimiento: ${selectedProcedure.name}` : ''}${budgetObservation.trim() ? `\nObservación: ${budgetObservation.trim()}` : ''}`;
        
        if (budgetPaid) {
          budgetLogText += `\n\n✅ PRESUPUESTO PAGADO\nN° Boleta: ${budgetReceiptNumber.trim()}`;
        }
      }

      // Nota: el Purchase por pago manual no se envía aquí. Solo se envía Purchase
      // real cuando se paga desde Caja (TicketVenta) o Webpay (BookingSuccess).
      
      const newLog = {
        userName: userData?.name || userData?.email || 'Usuario',
        userEmail: currentUser?.email || '',
        timestamp: new Date(),
        text: budgetLogText
      };

      const updatedLogs = [...logs, newLog];

      // Determinar el nuevo estado de la solicitud (solo si se marca como pagado)
      let newStatus = formData.status;
      if (budgetPaid && (isNewPayment || !editingBudget)) {
        newStatus = 'Atendido';
      } else if (!editingBudget && !budgetPaid) {
        newStatus = 'Presupuesto Enviado';
      }

      // Actualizar estado de la solicitud y bitácora
      await updateDocument('contact-requests', id, {
        status: newStatus,
        logs: updatedLogs
      });

      // Actualizar estados locales
      setLogs(updatedLogs);
      setFormData(prev => ({ ...prev, status: newStatus }));
      
      // Recargar presupuestos desde BD
      const budgetsData = await getCollection('budgets');
      const patientBudgets = budgetsData
        .filter(b => b.patientId === request.patientId)
        .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.() || 0);
      setBudgets(patientBudgets);
      
      handleCloseBudgetDialog();
      
      let successMessage = editingBudget ? 'Presupuesto actualizado exitosamente' : 'Presupuesto agregado exitosamente';
      if (budgetPaid && (isNewPayment || !editingBudget)) {
        successMessage = 'Presupuesto marcado como pagado. Estado cambiado a Atendido';
      }
      
      setSnackbar({ 
        open: true, 
        message: successMessage, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error saving budget:', error);
      setSnackbar({ open: true, message: 'Error al guardar presupuesto', severity: 'error' });
    }
  };

  const handleOpenTeleconsultDialog = () => {
    setOpenTeleconsultDialog(true);
  };

  const handleCloseTeleconsultDialog = () => {
    setOpenTeleconsultDialog(false);
    // Limpiar campos
    setTeleconsultDate('');
    setTeleconsultTime('');
    setTeleconsultDuration(30);
    setTeleconsultReason('');
    setTeleconsultNotes('');
  };

  const generateMeetLink = () => {
    // Generar un ID único simple para el link de Meet
    const meetId = Math.random().toString(36).substring(2, 15);
    return `https://meet.google.com/${meetId.slice(0, 3)}-${meetId.slice(3, 7)}-${meetId.slice(7, 10)}`;
  };

  const handleAddTeleconsult = async () => {
    if (!teleconsultDate || !teleconsultTime || !teleconsultReason.trim()) {
      setSnackbar({ open: true, message: 'Por favor complete fecha, hora y motivo de la teleconsulta', severity: 'warning' });
      return;
    }

    try {
      const meetLink = generateMeetLink();
      const appointmentDateTime = new Date(`${teleconsultDate}T${teleconsultTime}`);

      const newTeleconsult = {
        date: teleconsultDate,
        time: teleconsultTime,
        dateTime: appointmentDateTime,
        duration: teleconsultDuration,
        reason: teleconsultReason.trim(),
        notes: teleconsultNotes.trim(),
        meetLink: meetLink,
        veterinarian: formData.assignedTo || userData?.email || 'Sin asignar',
        veterinarianName: users.find(u => u.email === formData.assignedTo)?.name || 'Sin asignar',
        status: 'Agendada',
        createdBy: userData?.name || userData?.email || 'Usuario',
        createdByEmail: currentUser?.email || '',
        createdAt: new Date(),
        tutorEmail: owner?.email || '',
        tutorName: owner?.fullName || '',
        patientName: patient?.name || ''
      };

      const updatedTeleconsults = [...teleconsults, newTeleconsult];

      // Crear entrada en bitácora
      const teleconsultLogText = `Teleconsulta agendada:\nFecha: ${new Date(appointmentDateTime).toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}\nHora: ${teleconsultTime}\nDuración: ${teleconsultDuration} minutos\nVeterinario: ${newTeleconsult.veterinarianName}\nMotivo: ${teleconsultReason.trim()}\n\n🎥 Link de videollamada:\n${meetLink}${teleconsultNotes.trim() ? `\n\nNotas: ${teleconsultNotes.trim()}` : ''}`;
      
      const newLog = {
        userName: userData?.name || userData?.email || 'Usuario',
        userEmail: currentUser?.email || '',
        timestamp: new Date(),
        text: teleconsultLogText
      };

      const updatedLogs = [...logs, newLog];

      // Actualizar en Firestore
      await updateDocument('contact-requests', id, {
        teleconsults: updatedTeleconsults,
        status: 'Agendado',
        logs: updatedLogs
      });

      // Actualizar estados locales
      setTeleconsults(updatedTeleconsults);
      setLogs(updatedLogs);
      setFormData(prev => ({ ...prev, status: 'Agendado' }));
      
      // Guardar la teleconsulta actual para mostrar opciones de email/calendario
      setCurrentTeleconsult(newTeleconsult);
      
      handleCloseTeleconsultDialog();
      setShowEmailDialog(true); // Mostrar diálogo con opciones
      setSnackbar({ open: true, message: 'Teleconsulta agendada exitosamente', severity: 'success' });
    } catch (error) {
      console.error('Error adding teleconsult:', error);
      setSnackbar({ open: true, message: 'Error al agendar teleconsulta', severity: 'error' });
    }
  };

  const handleDownloadICS = () => {
    if (!currentTeleconsult) return;

    const startDate = new Date(`${currentTeleconsult.date}T${currentTeleconsult.time}`);
    const endDate = new Date(startDate.getTime() + currentTeleconsult.duration * 60000);

    const eventData = {
      title: `Teleconsulta - ${currentTeleconsult.patientName}`,
      description: `Teleconsulta Veterinaria\n\nPaciente: ${currentTeleconsult.patientName}\nTutor: ${currentTeleconsult.tutorName}\nVeterinario: ${currentTeleconsult.veterinarianName}\nMotivo: ${currentTeleconsult.reason}${currentTeleconsult.notes ? `\n\nNotas: ${currentTeleconsult.notes}` : ''}`,
      location: 'Videollamada Google Meet',
      startDate,
      endDate,
      organizerEmail: currentTeleconsult.createdByEmail,
      organizerName: currentTeleconsult.createdBy,
      attendeeEmail: currentTeleconsult.tutorEmail,
      attendeeName: currentTeleconsult.tutorName,
      meetLink: currentTeleconsult.meetLink
    };

    const icsContent = generateICSFile(eventData);
    downloadICSFile(icsContent, `teleconsulta_${currentTeleconsult.patientName}_${currentTeleconsult.date}.ics`);
    setSnackbar({ open: true, message: 'Archivo .ics descargado. Envíelo al tutor para que lo agregue a su calendario', severity: 'success' });
  };

  const handleOpenGoogleCalendar = () => {
    if (!currentTeleconsult) return;

    const startDate = new Date(`${currentTeleconsult.date}T${currentTeleconsult.time}`);
    const endDate = new Date(startDate.getTime() + currentTeleconsult.duration * 60000);

    const eventData = {
      title: `Teleconsulta - ${currentTeleconsult.patientName}`,
      description: `Teleconsulta Veterinaria\n\nPaciente: ${currentTeleconsult.patientName}\nTutor: ${currentTeleconsult.tutorName}\nVeterinario: ${currentTeleconsult.veterinarianName}\nMotivo: ${currentTeleconsult.reason}${currentTeleconsult.notes ? `\n\nNotas: ${currentTeleconsult.notes}` : ''}`,
      location: 'Videollamada Google Meet',
      startDate,
      endDate,
      meetLink: currentTeleconsult.meetLink
    };

    const calendarLink = generateGoogleCalendarLink(eventData);
    window.open(calendarLink, '_blank');
  };

  const handleCopyEmail = async () => {
    if (!currentTeleconsult) return;

    const startDate = new Date(`${currentTeleconsult.date}T${currentTeleconsult.time}`);
    const dateFormatted = startDate.toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    const emailData = {
      tutorName: currentTeleconsult.tutorName,
      patientName: currentTeleconsult.patientName,
      date: dateFormatted,
      time: currentTeleconsult.time,
      duration: currentTeleconsult.duration,
      veterinarianName: currentTeleconsult.veterinarianName,
      reason: currentTeleconsult.reason,
      meetLink: currentTeleconsult.meetLink,
      notes: currentTeleconsult.notes
    };

    const { subject, body } = generateEmailTemplate(emailData);
    const fullEmail = `Asunto: ${subject}\n\n${body}`;

    const copied = await copyToClipboard(fullEmail);
    if (copied) {
      setSnackbar({ open: true, message: 'Email copiado al portapapeles. Péguelo en su cliente de correo', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Error al copiar. Intente nuevamente', severity: 'error' });
    }
  };

  const handleCloseEmailDialog = () => {
    setShowEmailDialog(false);
    setCurrentTeleconsult(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!request) {
    return <Typography sx={{ m: 2 }}>No hay datos de la solicitud para mostrar.</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Chip
            label={request.source || 'Manual'}
            size="small"
            sx={{ bgcolor: getLeadColor(request.source), color: '#fff', mb: 1, fontWeight: 600 }}
          />
          <Typography variant="h4" gutterBottom>Detalle de la Solicitud</Typography>
        </Box>
        <Box>
          <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mr: 2 }}>Volver</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>Guardar</Button>
        </Box>
      </Box>
      <Grid container spacing={3}>
        {/* Patient Information */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Card>
            <CardHeader title="Información del Paciente" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField 
                    label="Nombre" 
                    name="patientName" 
                    value={formData.patientName} 
                    onChange={handleInputChange}
                    fullWidth 
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 5
                  }}>
                  <TextField 
                    label="Fecha de Nacimiento" 
                    name="birthDate" 
                    type="date"
                    value={formData.birthDate} 
                    onChange={handleInputChange} 
                    fullWidth 
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3
                  }}>
                  <TextField 
                    label="Edad" 
                    name="edad" 
                    value={formData.edad} 
                    fullWidth 
                    disabled
                    helperText="Auto"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 4
                  }}>
                  <TextField 
                    select 
                    label="Especie" 
                    name="species" 
                    value={formData.species} 
                    onChange={handleInputChange} 
                    fullWidth
                  >
                    <MenuItem value="Canino">Canino</MenuItem>
                    <MenuItem value="Felino">Felino</MenuItem>
                  </TextField>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 8
                  }}>
                  <TextField 
                    select 
                    label="Raza" 
                    name="breed" 
                    value={formData.breed || ''} 
                    onChange={handleInputChange} 
                    fullWidth
                    disabled={!formData.species}
                    helperText={
                      !formData.species 
                        ? 'Selecciona primero una especie' 
                        : breeds.length === 0 
                          ? 'No hay razas cargadas. Ve a Configuración → Razas para cargarlas' 
                          : `${breeds.filter(b => b.species === formData.species).length} razas disponibles`
                    }
                  >
                    <MenuItem value="">Seleccionar...</MenuItem>
                    {formData.species && breeds.filter(b => b.species === formData.species).length === 0 && (
                      <MenuItem disabled>No hay razas de {formData.species} cargadas</MenuItem>
                    )}
                    {breeds
                      .filter(b => b.species === formData.species)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((breed) => (
                        <MenuItem key={breed.id} value={breed.name}>
                          {breed.name}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 4
                  }}>
                  <TextField 
                    select 
                    label="Género" 
                    name="genero" 
                    value={formData.genero || ''} 
                    onChange={handleInputChange} 
                    fullWidth
                  >
                    <MenuItem value="">Seleccionar...</MenuItem>
                    <MenuItem value="Macho">Macho</MenuItem>
                    <MenuItem value="Hembra">Hembra</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Sucursal"
                    value={branchName(
                      request?.branchId ||
                      patient?.branchId ||
                      (patient?.branchIds || [])[0] ||
                      owner?.branchId ||
                      (owner?.branchIds || [])[0] ||
                      ''
                    ) || 'Sin sucursal'}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
              {patient?.id && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.open(`/patients/${patient.id}/record`, 'fichaPaciente', 'width=1200,height=800,scrollbars=yes,resizable=yes')}
                  >
                    Ver ficha del paciente
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Owner Information */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Card>
            <CardHeader title="Información del Tutor" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField 
                    label="Nombre" 
                    name="ownerName" 
                    value={formData.ownerName} 
                    onChange={handleInputChange} 
                    fullWidth 
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField 
                    label="RUT" 
                    name="rut" 
                    value={formData.rut} 
                    onChange={handleInputChange} 
                    fullWidth 
                    error={!!rutError}
                    helperText={rutError || 'Formato: 12345678-9'}
                    placeholder="12345678-9"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField 
                    label="Celular" 
                    name="celular" 
                    value={formData.celular} 
                    onChange={handleInputChange} 
                    fullWidth 
                    placeholder="+569 1234 5678"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField 
                    label="Email" 
                    name="mail" 
                    value={formData.mail} 
                    onChange={handleInputChange} 
                    fullWidth 
                    type="email" 
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField 
                    select
                    label="Región" 
                    name="region" 
                    value={formData.region} 
                    onChange={handleInputChange} 
                    fullWidth 
                  >
                    <MenuItem value="">Seleccionar región...</MenuItem>
                    {Object.keys(REGIONES_COMUNAS).map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField 
                    select
                    label="Comuna" 
                    name="comuna" 
                    value={formData.comuna} 
                    onChange={handleInputChange} 
                    fullWidth 
                    disabled={!formData.region}
                    helperText={!formData.region ? 'Selecciona primero una región' : ''}
                  >
                    <MenuItem value="">Seleccionar comuna...</MenuItem>
                    {formData.region && REGIONES_COMUNAS[formData.region]?.map((comuna) => (
                      <MenuItem key={comuna} value={comuna}>
                        {comuna}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12}>
                  <TextField 
                    select
                    label="Convenio" 
                    name="convenio" 
                    value={formData.convenio || ''} 
                    onChange={handleInputChange} 
                    fullWidth 
                    helperText={convenios.length === 0 ? 'No hay convenios cargados. Ve a Configuración → Convenios' : `${convenios.length} convenios disponibles`}
                  >
                    <MenuItem value="">Seleccionar convenio...</MenuItem>
                    {convenios
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((convenio) => (
                        <MenuItem key={convenio.id} value={convenio.name}>
                          {convenio.name} {convenio.type && `(${convenio.type})`}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Request Information */}
        <Grid size={12}>
          <Card>
            <CardHeader title="Información de la Solicitud" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={6}><TextField label="Fecha Solicitud" value={request.createdAt?.toDate().toLocaleDateString() || 'N/A'} fullWidth disabled /></Grid>
                <Grid size={6}><TextField label="Fuente Contacto" value={request.source || 'N/A'} fullWidth disabled /></Grid>
                <Grid size={12}>
                  <TextField
                    label="Mensaje Contacto (Necesidad original)"
                    value={request.additionalInfo || request.attentionNeed || 'N/A'}
                    fullWidth
                    multiline
                    rows={2}
                    disabled
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    select
                    label="Estado"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    fullWidth
                  >
                    <MenuItem value="Nuevo">Nuevo</MenuItem>
                    <MenuItem value="Asignado">Asignado</MenuItem>
                    <MenuItem value="Contactado">Contactado</MenuItem>
                    <MenuItem value="Presupuesto Enviado">Presupuesto Enviado</MenuItem>
                    <MenuItem value="Agendado">Agendado</MenuItem>
                    <MenuItem value="Atendido">Atendido</MenuItem>
                    <MenuItem value="Cerrado">Cerrado</MenuItem>
                  </TextField>
                </Grid>
                {request.diagnosis && (
                  <Grid size={12}>
                    <TextField
                      label="Diagnóstico"
                      value={request.diagnosis}
                      fullWidth
                      disabled
                      multiline
                      rows={2}
                    />
                  </Grid>
                )}
                {clinicalRecord && (
                  <Grid size={12}>
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>Ficha SOAP</Typography>
                        {request.patientId && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(`/patients/${request.patientId}/consultation/${request.originId}`, 'soap', 'width=1200,height=800,scrollbars=yes,resizable=yes')}
                          >
                            Abrir ficha
                          </Button>
                        )}
                      </Box>
                      <Grid container spacing={1}>
                        <Grid size={6}><TextField label="Fecha" value={clinicalRecord.createdAt?.toDate?.().toLocaleDateString?.() || clinicalRecord.fecha || 'N/A'} fullWidth disabled size="small" /></Grid>
                        <Grid size={6}><TextField label="Veterinario" value={clinicalRecord.vetName || clinicalRecord.veterinarian || 'N/A'} fullWidth disabled size="small" /></Grid>
                        <Grid size={12}><TextField label="Motivo de consulta" value={clinicalRecord.motivoConsulta || 'N/A'} fullWidth disabled multiline rows={2} /></Grid>
                        <Grid size={12}><TextField label="Anamnesis" value={clinicalRecord.anamnesis || 'N/A'} fullWidth disabled multiline rows={2} /></Grid>
                        <Grid size={12}>
                          <TextField
                            label="Diagnósticos"
                            value={(clinicalRecord.diagnosticos || []).map((d) => d.descripcion || d).join(', ')}
                            fullWidth
                            disabled
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid size={12}><TextField label="Plan" value={clinicalRecord.plan || clinicalRecord.tratamiento || 'N/A'} fullWidth disabled multiline rows={2} /></Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}
                <Grid size={12}>
                  <TextField
                    select
                    label="Motivo de cierre"
                    name="closeReason"
                    value={formData.closeReason || ''}
                    onChange={handleInputChange}
                    fullWidth
                    helperText="Selecciona el motivo por el cual se cierra el caso"
                  >
                    <MenuItem value="">Sin motivo</MenuItem>
                    <MenuItem value="Caso Duplicado">Caso Duplicado</MenuItem>
                    <MenuItem value="Atendido ok">Atendido ok</MenuItem>
                    <MenuItem value="No puede pagar">No puede pagar</MenuItem>
                    <MenuItem value="No contesta">No contesta</MenuItem>
                    <MenuItem value="Se atendió en otra clínica">Se atendió en otra clínica</MenuItem>
                    <MenuItem value="Buscará segunda opinión">Buscará segunda opinión</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Request Management - Nueva Sección */}
        <Grid size={12}>
          <Card>
            <CardHeader title="Gestión de Solicitud" />
            <CardContent>
              <Grid container spacing={2}>
                {/* Asignado a */}
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <TextField 
                    select
                    label="Asignado a" 
                    name="assignedTo" 
                    value={formData.assignedTo || ''} 
                    onChange={handleInputChange} 
                    fullWidth
                    helperText={users.length === 0 ? 'No hay usuarios disponibles' : `${users.length} usuarios disponibles`}
                  >
                    <MenuItem value="">Sin asignar</MenuItem>
                    {users
                      .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''))
                      .map((user) => (
                        <MenuItem key={user.id} value={user.email}>
                          {user.name || user.email} {user.role && `(${user.role})`}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>

                {/* Necesidad */}
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <TextField 
                    select 
                    label="Necesidad (Actual)" 
                    name="attentionNeed" 
                    value={formData.attentionNeed || ''} 
                    onChange={handleInputChange} 
                    fullWidth
                    helperText={needs.length === 0 ? 'No hay necesidades cargadas. Ve a Configuración → Necesidades' : `${needs.length} necesidades disponibles`}
                  >
                    <MenuItem value="">Seleccionar necesidad...</MenuItem>
                    {needs
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((need) => (
                        <MenuItem key={need.id} value={need.name}>
                          {need.name}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>

                {/* Prioridad */}
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <TextField 
                    select 
                    label="Prioridad" 
                    name="priority" 
                    value={formData.priority || 'Media'} 
                    onChange={handleInputChange} 
                    fullWidth
                  >
                    <MenuItem value="Urgente">Urgente</MenuItem>
                    <MenuItem value="Alta">Alta</MenuItem>
                    <MenuItem value="Media">Media</MenuItem>
                    <MenuItem value="Normal">Normal</MenuItem>
                  </TextField>
                </Grid>

                {/* Próxima acción */}
                <Grid
                  size={{
                    xs: 12,
                    md: 8
                  }}>
                  <TextField
                    label="Próxima acción"
                    name="nextAction"
                    value={formData.nextAction || ''}
                    onChange={handleInputChange}
                    fullWidth
                    placeholder="Llamar al tutor, enviar presupuesto, etc."
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <TextField
                    label="Fecha próxima acción"
                    name="nextActionDate"
                    type="date"
                    value={formData.nextActionDate || ''}
                    onChange={handleInputChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Presupuestos y pagos reales */}
                <Grid size={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Presupuestos y Pagos" />
                  </Divider>
                  {presupuestos.length === 0 && sales.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 1 }}>No hay presupuestos ni pagos registrados para este paciente.</Alert>
                  ) : (
                    <List dense sx={{ bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                      {presupuestos.map((pre) => {
                        const sale = sales.find((s) => s.presupuestoId === pre.id && (s.estado || 'emitida') !== 'anulada');
                        const goalNames = [...new Set((pre.items || []).map((i) => i.goalName).filter(Boolean))];
                        const itemsText = (pre.items || []).map((i) => `${i.tipoPrestacion || i.name || 'Prestación'} x${i.cantidad || 1} ($${formatCurrency(String(i.subtotal || 0))})`).join(' · ');
                        return (
                          <ListItem key={pre.id} alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={600}>N° {pre.numero || '—'}</Typography>
                                  <Chip label={pre.estado || 'borrador'} size="small" color={pre.estado === 'pagado' ? 'success' : (pre.estado === 'enviado' ? 'info' : 'default')} />
                                  {sale && <Chip label={`Pago boleta ${sale.boletaNumber || '—'}`} size="small" color="success" />}
                                </Box>
                              }
                              secondary={
                                <Box component="span">
                                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                    Meta: {goalNames.join(', ') || '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                    {itemsText || 'Sin ítems'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                    Total: ${formatCurrency(String(pre.total || 0))} · {pre.items?.length || 0} ítems
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                      {sales.filter((s) => !presupuestos.some((p) => p.id === s.presupuestoId)).map((s) => (
                        <ListItem key={s.id}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>Venta boleta {s.boletaNumber || '—'}</Typography>
                                <Chip label={s.estado || 'emitida'} size="small" color={(s.estado || 'emitida') === 'anulada' ? 'error' : 'success'} />
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Total: ${formatCurrency(String(s.totalFinal || s.total || 0))}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Grid>

                {/* Sección de Acciones Estandarizadas */}
                <Grid size={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Acciones Estandarizadas" />
                  </Divider>
                </Grid>

                <Grid size={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate(`/presupuestos/nuevo?patientId=${patient?.id || ''}&contactRequestId=${id}&ownerId=${owner?.id || ''}`)}
                    >
                      Nuevo Presupuesto
                      {presupuestos.length > 0 && (
                        <Chip
                          label={presupuestos.length}
                          size="small"
                          sx={{ ml: 1, bgcolor: 'white', color: 'primary.main' }}
                        />
                      )}
                    </Button>
                    
                    <Button 
                      variant="contained" 
                      color="secondary"
                      onClick={handleOpenTeleconsultDialog}
                    >
                      Agendar Teleconsulta
                      {teleconsults.length > 0 && (
                        <Chip 
                          label={teleconsults.length}
                          size="small"
                          sx={{ ml: 1, bgcolor: 'white', color: 'secondary.main' }}
                        />
                      )}
                    </Button>
                    
                    <Button 
                      variant="contained" 
                      color="info"
                    >
                      Agendar Toma de Exámenes
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => window.open(`/scheduler?patientId=${request?.patientId || ''}`, 'agenda', 'width=1200,height=800,scrollbars=yes,resizable=yes')}
                    >
                      Agendar Consulta
                    </Button>

                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCloseCase}
                    >
                      Cerrar caso
                    </Button>

                    <Button 
                      variant="contained" 
                      color="warning"
                      onClick={() => navigate('/surgery-scheduler', { 
                        state: { 
                          patientData: patient,
                          ownerData: owner 
                        }
                      })}
                    >
                      Programar Cirugía
                    </Button>
                  </Box>
                </Grid>

                {/* Divider para Bitácora */}
                <Grid size={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Notas y Seguimiento" />
                  </Divider>
                </Grid>

                {/* Nueva entrada de notas */}
                <Grid size={12}>
                  <TextField 
                    label="Agregar nota de seguimiento" 
                    value={newLogText}
                    onChange={(e) => setNewLogText(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Escriba notas sobre el seguimiento del caso, llamadas realizadas, observaciones, etc..."
                  />
                </Grid>
                <Grid size={12}>
                  <Button 
                    variant="outlined" 
                    onClick={handleAddLog}
                    disabled={!newLogText.trim()}
                  >
                    Agregar Nota
                  </Button>
                </Grid>

                {/* Lista de bitácora */}
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Historial Completo (Acciones y Notas)
                  </Typography>
                  {logs.length === 0 ? (
                    <Alert severity="info">No hay entradas en la bitácora aún.</Alert>
                  ) : (
                    <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      {logs
                        .sort((a, b) => {
                          const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
                          const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
                          return dateB - dateA; // Más reciente primero
                        })
                        .map((log, index) => {
                          const logDate = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                          const formattedDate = logDate.toLocaleDateString('es-CL', { 
                            year: 'numeric', 
                            month: '2-digit', 
                            day: '2-digit' 
                          });
                          const formattedTime = logDate.toLocaleTimeString('es-CL', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          });
                          
                          return (
                            <React.Fragment key={index}>
                              <ListItem alignItems="flex-start">
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="subtitle2" component="span" color="primary">
                                        {log.userName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formattedDate} - {formattedTime}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                      sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                                    >
                                      {log.text}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              {index < logs.length - 1 && <Divider />}
                            </React.Fragment>
                          );
                        })}
                    </List>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para Agendar Teleconsulta */}
      <Dialog open={openTeleconsultDialog} onClose={handleCloseTeleconsultDialog} maxWidth="md" fullWidth>
        <DialogTitle>Agendar Teleconsulta</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Información del paciente y tutor */}
              <Grid size={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    <strong>Paciente:</strong> {patient?.name || 'N/A'} | <strong>Tutor:</strong> {owner?.fullName || 'N/A'} | <strong>Email:</strong> {owner?.email || 'N/A'}
                  </Typography>
                </Alert>
              </Grid>

              {/* Fecha */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Fecha de la Teleconsulta"
                  type="date"
                  value={teleconsultDate}
                  onChange={(e) => setTeleconsultDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: todayLocalStr() }}
                />
              </Grid>

              {/* Hora */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Hora"
                  type="time"
                  value={teleconsultTime}
                  onChange={(e) => setTeleconsultTime(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Duración */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  select
                  label="Duración"
                  value={teleconsultDuration}
                  onChange={(e) => setTeleconsultDuration(e.target.value)}
                  fullWidth
                >
                  <MenuItem value={15}>15 minutos</MenuItem>
                  <MenuItem value={30}>30 minutos</MenuItem>
                  <MenuItem value={45}>45 minutos</MenuItem>
                  <MenuItem value={60}>1 hora</MenuItem>
                </TextField>
              </Grid>

              {/* Veterinario asignado */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Veterinario Asignado"
                  value={users.find(u => u.email === formData.assignedTo)?.name || formData.assignedTo || 'Sin asignar'}
                  fullWidth
                  disabled
                  helperText="Asigne un veterinario en la sección de Gestión"
                />
              </Grid>

              {/* Motivo */}
              <Grid size={12}>
                <TextField
                  label="Motivo de la Teleconsulta"
                  value={teleconsultReason}
                  onChange={(e) => setTeleconsultReason(e.target.value)}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  placeholder="Ej: Control post-operatorio, consulta de seguimiento, evaluación de síntomas..."
                />
              </Grid>

              {/* Notas adicionales */}
              <Grid size={12}>
                <TextField
                  label="Notas Adicionales (Opcional)"
                  value={teleconsultNotes}
                  onChange={(e) => setTeleconsultNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Información adicional, preparación necesaria, documentos a revisar..."
                />
              </Grid>

              {/* Resumen previo */}
              {teleconsultDate && teleconsultTime && teleconsultReason && (
                <Grid size={12}>
                  <Alert severity="success">
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Resumen de la Teleconsulta:</strong>
                    </Typography>
                    <Typography variant="body2">
                      📅 <strong>Fecha:</strong> {new Date(teleconsultDate).toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}<br />
                      ⏰ <strong>Hora:</strong> {teleconsultTime} hrs<br />
                      ⏱️ <strong>Duración:</strong> {teleconsultDuration} minutos<br />
                      👨‍⚕️ <strong>Veterinario:</strong> {users.find(u => u.email === formData.assignedTo)?.name || 'Sin asignar'}<br />
                      📋 <strong>Motivo:</strong> {teleconsultReason}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Se generará un link de Google Meet y se enviará al tutor: {owner?.email || 'N/A'}
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Lista de teleconsultas previas */}
              {teleconsults.length > 0 && (
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Teleconsultas Agendadas ({teleconsults.length}):
                  </Typography>
                  <List dense sx={{ bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                    {teleconsults.map((tc, index) => {
                      const tcDate = tc.dateTime?.toDate ? tc.dateTime.toDate() : new Date(tc.dateTime);
                      return (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${tcDate.toLocaleDateString('es-CL')} - ${tc.time} hrs (${tc.duration} min)`}
                            secondary={
                              <>
                                <Typography variant="caption" display="block">
                                  <strong>Motivo:</strong> {tc.reason}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Veterinario:</strong> {tc.veterinarianName} | <strong>Estado:</strong> {tc.status}
                                </Typography>
                                <Typography variant="caption" display="block" color="primary">
                                  🎥 <a href={tc.meetLink} target="_blank" rel="noopener noreferrer">Link de Meet</a>
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTeleconsultDialog}>Cancelar</Button>
          <Button 
            onClick={handleAddTeleconsult} 
            variant="contained" 
            color="secondary"
            disabled={!teleconsultDate || !teleconsultTime || !teleconsultReason.trim()}
          >
            Agendar Teleconsulta
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog para Envío de Email y Calendario */}
      <Dialog open={showEmailDialog} onClose={handleCloseEmailDialog} maxWidth="md" fullWidth>
        <DialogTitle>📧 Notificar al Tutor y Agregar a Calendario</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {currentTeleconsult && (
              <>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    ✅ Teleconsulta agendada exitosamente
                  </Typography>
                  <Typography variant="body2">
                    <strong>Paciente:</strong> {currentTeleconsult.patientName}<br />
                    <strong>Fecha:</strong> {new Date(`${currentTeleconsult.date}T${currentTeleconsult.time}`).toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} - {currentTeleconsult.time} hrs<br />
                    <strong>Tutor:</strong> {currentTeleconsult.tutorName} ({currentTeleconsult.tutorEmail})
                  </Typography>
                </Alert>

                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Opciones de Notificación y Calendario:
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Opción 1: Copiar Email */}
                  <Grid size={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          📧 1. Enviar Email al Tutor
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Copia el email pre-formateado y envíalo desde tu cliente de correo (Gmail, Outlook, etc.)
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={handleCopyEmail}
                          fullWidth
                        >
                          Copiar Email al Portapapeles
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Incluye: Datos de la cita, link de Meet, instrucciones
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Opción 2: Google Calendar */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          📅 2. Agregar a Google Calendar
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Si el tutor tiene Gmail, ábrele esta página para agregar el evento
                        </Typography>
                        <Button 
                          variant="outlined" 
                          color="primary"
                          onClick={handleOpenGoogleCalendar}
                          fullWidth
                        >
                          Abrir Google Calendar
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Se abre en una nueva pestaña
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Opción 3: Descargar .ics */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          📥 3. Descargar Archivo .ics
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Compatible con Outlook, Apple Calendar, y otros
                        </Typography>
                        <Button 
                          variant="outlined" 
                          color="secondary"
                          onClick={handleDownloadICS}
                          fullWidth
                        >
                          Descargar .ics
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Envíalo al tutor por email
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Información del link de Meet */}
                  <Grid size={12}>
                    <Alert severity="info">
                      <Typography variant="subtitle2" gutterBottom>
                        🎥 Link de Videollamada:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          bgcolor: 'background.paper', 
                          p: 1, 
                          borderRadius: 1,
                          wordBreak: 'break-all'
                        }}
                      >
                        {currentTeleconsult.meetLink}
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={() => copyToClipboard(currentTeleconsult.meetLink)}
                        sx={{ mt: 1 }}
                      >
                        Copiar Link
                      </Button>
                    </Alert>
                  </Grid>

                  {/* Instrucciones */}
                  <Grid size={12}>
                    <Alert severity="warning">
                      <Typography variant="subtitle2" gutterBottom>
                        📝 Instrucciones:
                      </Typography>
                      <Typography variant="body2" component="div">
                        1. <strong>Copiar email</strong> y enviarlo al tutor<br />
                        2. Si tiene Gmail: <strong>Compartir link de Google Calendar</strong> para que agregue la cita<br />
                        3. Si tiene otro calendario: <strong>Enviar archivo .ics</strong> adjunto<br />
                        4. El link de Meet está incluido en todas las opciones
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} variant="contained">
            Listo
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cerrar caso</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Motivo de cierre"
            value={closeDialogReason}
            onChange={(e) => setCloseDialogReason(e.target.value)}
            fullWidth
            margin="dense"
            helperText="Selecciona el motivo por el cual se cierra el caso"
          >
            <MenuItem value="">Sin motivo</MenuItem>
            <MenuItem value="Caso Duplicado">Caso Duplicado</MenuItem>
            <MenuItem value="Atendido ok">Atendido ok</MenuItem>
            <MenuItem value="No puede pagar">No puede pagar</MenuItem>
            <MenuItem value="No contesta">No contesta</MenuItem>
            <MenuItem value="Se atendió en otra clínica">Se atendió en otra clínica</MenuItem>
            <MenuItem value="Buscará segunda opinión">Buscará segunda opinión</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmCloseCase} variant="contained" color="error">Cerrar caso</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactRequestDetail;
